import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphTitle,
  GraphContainer,
  EmptyState,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';
import styled from 'styled-components';

// Styled component for location tab grid
const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Custom bar shape with rounded top corners and shadow, with varying opacity
const RoundedBar = (props: any) => {
  const { fill, x, y, width, height, payload, index } = props;
  const radius = 8;
  
  // Calculate opacity based on index (each bar gets slightly less opacity)
  // Use index from props, or fallback to 0, with a minimum opacity of 0.6
  const barIndex = index !== undefined ? index : 0;
  const opacity = Math.max(0.6, 1.0 - barIndex * 0.03);
  
  // Create path for rectangle with only top corners rounded
  // Path starts from bottom-left, goes up, rounds top-left, across top, rounds top-right, down to bottom-right, then closes
  const path = `
    M ${x},${y + height}
    L ${x},${y + radius}
    Q ${x},${y} ${x + radius},${y}
    L ${x + width - radius},${y}
    Q ${x + width},${y} ${x + width},${y + radius}
    L ${x + width},${y + height}
    Z
  `;
  
  return (
    <g>
      <path
        d={path}
        fill={fill}
        fillOpacity={opacity}
        style={{
          filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.15))',
        }}
      />
    </g>
  );
};

interface TrainingSession {
  _id: string;
  date: string;
  location: string;
  hillSize?: string;
  trainingType: string;
  jumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    distance?: number;
  }>;
  competitionJumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    firstRoundDistance?: number;
    secondRoundDistance?: number;
  }>;
  groundTrainings?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    trainingType?: string;
    intensity?: number;
  }>;
  athleteIds?: Array<string | { _id: string; firstName: string; lastName: string }>;
}

type TabType = 'sessions' | 'location' | 'athlete';

interface TrainingSessionsAnalyticsProps {
  isJumperMode?: boolean;
}

const TrainingSessionsAnalytics: React.FC<TrainingSessionsAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState<TabType>((savedFilters?.activeTab as TabType) || 'sessions');
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInitialMount = useRef(true);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data states for different tabs
  const [trainingSessionsPieData, setTrainingSessionsPieData] = useState<any[]>([]);
  const [sessionsPerLocation, setSessionsPerLocation] = useState<any[]>([]);
  const [avgTrainingSessionsPerMonth, setAvgTrainingSessionsPerMonth] = useState<any[]>([]);
  const [avgCompetitionSessionsPerMonth, setAvgCompetitionSessionsPerMonth] = useState<any[]>([]);
  const [trainingSessionsByAthlete, setTrainingSessionsByAthlete] = useState<any[]>([]);
  const [locationBarChartData, setLocationBarChartData] = useState<any[]>([]);
  const [avgDistancesPerLocation, setAvgDistancesPerLocation] = useState<any[]>([]);
  const [bestDistancesPerLocation, setBestDistancesPerLocation] = useState<any[]>([]);
  const [athleteParticipationPerMonth, setAthleteParticipationPerMonth] = useState<any[]>([]);
  const [athleteParticipationThisSeason, setAthleteParticipationThisSeason] = useState<any[]>([]);
  const [bestDistancesPerAthlete, setBestDistancesPerAthlete] = useState<any[]>([]);
  const [avgDistancesPerAthlete, setAvgDistancesPerAthlete] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
            // Set default tab to 'sessions' in jumper mode (no 'athlete' tab)
            setActiveTab('sessions');
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, sessionsResponse, bananaCupsData] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll().catch(() => []), // Handle Banana Cup fetch errors gracefully
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        // Filter athletes by plan requirement (analytics requires 'hs130' plan)
        setAthletes(athletesData);
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setBananaCups(bananaCupsData || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJumperMode]);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      activeTab,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, activeTab]);

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to get month key from date string
  const getMonthKey = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Helper function to get season start date
  const getSeasonStartDate = (): string => {
    const today = new Date();
    const currentMonth = today.getMonth();
    if (currentMonth >= 10) { // November or December
      return `${today.getFullYear()}-11-01`;
    } else { // January to October
      return `${today.getFullYear() - 1}-11-01`;
    }
  };

  // Process data based on active tab
  useEffect(() => {
    if (!sessions.length) return;
    
    // Filter sessions by date range (for most tabs)
    const filteredSessions = (startDate && endDate) 
      ? sessions.filter(session => {
          if (!isDateInRange(session.date, startDate, endDate)) return false;
          return true;
        })
      : sessions;
    
    // Don't process data if date range is not set (only for sessions and location tabs)
    if ((activeTab === 'sessions' || activeTab === 'location') && (!startDate || !endDate)) {
      setTrainingSessionsPieData([]);
      setSessionsPerLocation([]);
      setTrainingSessionsByAthlete([]);
      setAvgTrainingSessionsPerMonth([]);
      setAvgCompetitionSessionsPerMonth([]);
      setLocationBarChartData([]);
      setAvgDistancesPerLocation([]);
      setBestDistancesPerLocation([]);
      return;
    }

    if (activeTab === 'sessions') {
      // SESSIONS TAB - Only training sessions (not competition or banana_cup)
      const trainingOnlySessions = filteredSessions.filter(
        s => s.trainingType === 'ski_jumping' || s.trainingType === 'ground_training' || s.trainingType === 'cross_country'
      );
      
      // Filter by selected athletes - require at least one athlete selected
      const athleteFilteredSessions = selectedAthletes.length > 0
        ? trainingOnlySessions.filter(session => {
            if (session.athleteIds && session.athleteIds.length > 0) {
              return session.athleteIds.some(athleteId => {
                const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
                return selectedAthletes.includes(id);
              });
            }
            return false;
          })
        : [];
      
      // Pie chart: Compare number of ski jumping, ground training, and cross country sessions
      // Only count sessions that have actual data (matches Training Sessions page logic)
      const skiJumpingCount = athleteFilteredSessions.filter(s => 
        s.trainingType === 'ski_jumping' && s.jumps && s.jumps.length > 0
      ).length;
      const groundTrainingCount = athleteFilteredSessions.filter(s => 
        s.trainingType === 'ground_training' && s.groundTrainings && s.groundTrainings.length > 0
      ).length;
      const crossCountryCount = athleteFilteredSessions.filter(s => 
        s.trainingType === 'cross_country'
      ).length;
      
      const pieData = [
        { name: 'Ski Jumping', value: skiJumpingCount },
        { name: 'Ground Training', value: groundTrainingCount },
        { name: 'Cross Country', value: crossCountryCount },
      ].filter(item => item.value > 0); // Only show categories with data
      setTrainingSessionsPieData(pieData);
      
      // 1. List of how many times training sessions was done on specific LOCATION (only training sessions)
      // Note: trainingOnlySessions already excludes pre-planned sessions
      const locationCountMap = new Map<string, number>();
      trainingOnlySessions.forEach(session => {
        const count = locationCountMap.get(session.location) || 0;
        locationCountMap.set(session.location, count + 1);
      });
      const locationList = Array.from(locationCountMap.entries())
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count);
      setSessionsPerLocation(locationList);

      // 2. Bar graph: Number of training sessions for selected athletes only
      const athleteSessionCountMap = new Map<string, number>();
      athleteFilteredSessions.forEach(session => {
        const sessionAthletes = session.athleteIds || [];
        sessionAthletes.forEach(athleteId => {
          const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
          // Only count if athlete is selected
          if (selectedAthletes.length === 0 || selectedAthletes.includes(id)) {
            const count = athleteSessionCountMap.get(id) || 0;
            athleteSessionCountMap.set(id, count + 1);
          }
        });
      });
      
      const athleteSessionData = Array.from(athleteSessionCountMap.entries())
        .map(([athleteId, count]) => {
          const athlete = athletes.find(a => a._id === athleteId);
          return {
            athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
            athleteId,
            count,
          };
        })
        .sort((a, b) => b.count - a.count);
      setTrainingSessionsByAthlete(athleteSessionData);

      // 3. Line graph: Average number of training sessions per month PER ATHLETE
      const athleteMonthlyMap = new Map<string, Map<string, number>>();
      
      // Get athletes to process (selected or all)
      const athletesToProcess = selectedAthletes.length > 0
        ? athletes.filter(a => selectedAthletes.includes(a._id))
        : athletes;
      
      athleteFilteredSessions.forEach(session => {
        const monthKey = getMonthKey(session.date);
        const sessionAthletes = session.athleteIds || [];
        
        sessionAthletes.forEach(athleteId => {
          const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
          if (!athleteMonthlyMap.has(id)) {
            athleteMonthlyMap.set(id, new Map());
          }
          const monthMap = athleteMonthlyMap.get(id)!;
          const count = monthMap.get(monthKey) || 0;
          monthMap.set(monthKey, count + 1);
        });
      });

      // Generate complete date range for line chart
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const monthData: any[] = [];
        const current = new Date(start);
        
        // Get all unique months
        const allMonths: string[] = [];
        while (current <= end) {
          const monthKey = getMonthKey(current.toISOString().split('T')[0]);
          if (!allMonths.includes(monthKey)) {
            allMonths.push(monthKey);
          }
          current.setMonth(current.getMonth() + 1);
          current.setDate(1);
        }
        
        // Build data for each month with each athlete
        allMonths.forEach(monthKey => {
          const dataPoint: any = { month: monthKey };
          athletesToProcess.forEach(athlete => {
            const monthMap = athleteMonthlyMap.get(athlete._id);
            const count = monthMap ? (monthMap.get(monthKey) || 0) : 0;
            const athleteName = `${athlete.firstName} ${athlete.lastName}`;
            dataPoint[athleteName] = count > 0 ? count : null;
          });
          monthData.push(dataPoint);
        });
        
        setAvgTrainingSessionsPerMonth(monthData);
      } else {
        // Get all months from data
        const allMonthsSet = new Set<string>();
        athleteMonthlyMap.forEach(monthMap => {
          monthMap.forEach((_, monthKey) => allMonthsSet.add(monthKey));
        });
        const allMonths = Array.from(allMonthsSet).sort();
        
        const monthData = allMonths.map(monthKey => {
          const dataPoint: any = { month: monthKey };
          athletesToProcess.forEach(athlete => {
            const monthMap = athleteMonthlyMap.get(athlete._id);
            const count = monthMap ? (monthMap.get(monthKey) || 0) : 0;
            const athleteName = `${athlete.firstName} ${athlete.lastName}`;
            dataPoint[athleteName] = count > 0 ? count : null;
          });
          return dataPoint;
        });
        setAvgTrainingSessionsPerMonth(monthData);
      }

      // 4. Line graph: Average number of competition sessions per month
      const competitionSessionsByMonth = new Map<string, number>();
      filteredSessions
        .filter(s => s.trainingType === 'competition')
        .forEach(session => {
          const monthKey = getMonthKey(session.date);
          const count = competitionSessionsByMonth.get(monthKey) || 0;
          competitionSessionsByMonth.set(monthKey, count + 1);
        });

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const monthData: any[] = [];
        const current = new Date(start);
        
        while (current <= end) {
          const monthKey = getMonthKey(current.toISOString().split('T')[0]);
          const count = competitionSessionsByMonth.get(monthKey) || 0;
          monthData.push({
            month: monthKey,
            count: count > 0 ? count : null,
          });
          
          current.setMonth(current.getMonth() + 1);
          current.setDate(1);
        }
        setAvgCompetitionSessionsPerMonth(monthData);
      } else {
        const monthData = Array.from(competitionSessionsByMonth.entries())
          .map(([month, count]) => ({ month, count }))
          .sort((a, b) => a.month.localeCompare(b.month));
        setAvgCompetitionSessionsPerMonth(monthData);
      }

    } else if (activeTab === 'location') {
      // LOCATION TAB - Only training sessions (not competition or banana_cup)
      const trainingOnlySessions = filteredSessions.filter(
        s => s.trainingType === 'ski_jumping' || s.trainingType === 'ground_training' || s.trainingType === 'cross_country'
      );

      // 1. Bar chart: Where most training sessions were done (only training sessions)
      const locationCountMap = new Map<string, number>();
      trainingOnlySessions.forEach(session => {
        const count = locationCountMap.get(session.location) || 0;
        locationCountMap.set(session.location, count + 1);
      });
      const locationBarData = Array.from(locationCountMap.entries())
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count);
      setLocationBarChartData(locationBarData);

      // 2. List: Average distances on different locations
      const locationDistanceMap = new Map<string, { sum: number; count: number }>();
      const locationBestDistanceMap = new Map<string, number>();
      
      trainingOnlySessions.forEach(session => {
        // Process regular jumps
        if (session.jumps) {
          session.jumps.forEach(jump => {
            if (jump.distance) {
              const existing = locationDistanceMap.get(session.location) || { sum: 0, count: 0 };
              existing.sum += jump.distance;
              existing.count += 1;
              locationDistanceMap.set(session.location, existing);
              
              // Track best distance
              const currentBest = locationBestDistanceMap.get(session.location) || 0;
              if (jump.distance > currentBest) {
                locationBestDistanceMap.set(session.location, jump.distance);
              }
            }
          });
        }
      });

      const avgDistances = Array.from(locationDistanceMap.entries())
        .map(([location, data]) => ({
          location,
          averageDistance: data.count > 0 ? Math.round((data.sum / data.count) * 10) / 10 : 0,
        }))
        .sort((a, b) => b.averageDistance - a.averageDistance);
      setAvgDistancesPerLocation(avgDistances);

      // 3. List: Best distances at Location
      const bestDistances = Array.from(locationBestDistanceMap.entries())
        .map(([location, distance]) => ({
          location,
          distance,
        }))
        .sort((a, b) => b.distance - a.distance);
      setBestDistancesPerLocation(bestDistances);

    } else if (!isJumperMode && activeTab === 'athlete') {
      // ATHLETE TAB

      // Filter by selected athletes - require at least one athlete selected
      const athleteFilteredSessions = selectedAthletes.length > 0
        ? filteredSessions.filter(session => {
            if (session.athleteIds && session.athleteIds.length > 0) {
              return session.athleteIds.some(athleteId => {
                const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
                return selectedAthletes.includes(id);
              });
            }
            return false;
          })
        : [];

      // 1. Bar chart: How many times each athlete was on training session on average per month
      const athleteMonthlyCountMap = new Map<string, Map<string, number>>();
      
      athleteFilteredSessions.forEach(session => {
        const monthKey = getMonthKey(session.date);
        const sessionAthletes = session.athleteIds || [];
        
        sessionAthletes.forEach(athleteId => {
          const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
          if (!athleteMonthlyCountMap.has(id)) {
            athleteMonthlyCountMap.set(id, new Map());
          }
          const monthMap = athleteMonthlyCountMap.get(id)!;
          const count = monthMap.get(monthKey) || 0;
          monthMap.set(monthKey, count + 1);
        });
      });

      // Calculate average per month for each athlete
      const athleteAvgPerMonth: any[] = [];
      athleteMonthlyCountMap.forEach((monthMap, athleteId) => {
        const athlete = athletes.find(a => a._id === athleteId);
        if (!athlete) return;
        
        const counts = Array.from(monthMap.values());
        const average = counts.length > 0 
          ? Math.round((counts.reduce((a, b) => a + b, 0) / counts.length) * 10) / 10
          : 0;
        
        athleteAvgPerMonth.push({
          athlete: `${athlete.firstName} ${athlete.lastName}`,
          athleteId,
          average: average > 0 ? average : null,
        });
      });
      setAthleteParticipationPerMonth(athleteAvgPerMonth.sort((a, b) => (b.average || 0) - (a.average || 0)));

      // 2. Bar chart: How many times each athlete was on training session this season
      // For season data, filter from all sessions (not just date range filtered) to ensure we get all season data
      const seasonStart = getSeasonStartDate();
      const allAthleteFilteredSessions = selectedAthletes.length > 0
        ? sessions.filter(session => {
            if (session.athleteIds && session.athleteIds.length > 0) {
              return session.athleteIds.some(athleteId => {
                const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
                return selectedAthletes.includes(id);
              });
            }
            return false;
          })
        : [];
      const seasonSessions = allAthleteFilteredSessions.filter(s => s.date >= seasonStart);
      const athleteSeasonCountMap = new Map<string, number>();
      
      seasonSessions.forEach(session => {
        const sessionAthletes = session.athleteIds || [];
        sessionAthletes.forEach(athleteId => {
          const id = typeof athleteId === 'object' ? athleteId._id : athleteId;
          if (selectedAthletes.includes(id)) {
            const count = athleteSeasonCountMap.get(id) || 0;
            athleteSeasonCountMap.set(id, count + 1);
          }
        });
      });

      const athleteSeasonData = Array.from(athleteSeasonCountMap.entries())
        .map(([athleteId, count]) => {
          const athlete = athletes.find(a => a._id === athleteId);
          return {
            athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
            count,
          };
        })
        .sort((a, b) => b.count - a.count);
      setAthleteParticipationThisSeason(athleteSeasonData);

      // 3. List: Best distances for each athlete
      // Include distances from: training sessions, competition sessions (trial, 1st, 2nd round), and banana cup sessions (all rounds)
      const athleteBestDistanceMap = new Map<string, number>();
      
      athleteFilteredSessions.forEach(session => {
        // Process regular jumps from training sessions
        if (session.jumps) {
          session.jumps.forEach(jump => {
            if (jump.distance) {
              const athleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
              const currentBest = athleteBestDistanceMap.get(athleteId) || 0;
              if (jump.distance > currentBest) {
                athleteBestDistanceMap.set(athleteId, jump.distance);
              }
            }
          });
        }
        
        // Process competition jumps (trial, 1st, and 2nd round)
        if (session.competitionJumps) {
          session.competitionJumps.forEach(jump => {
            const athleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            
            // Trial round distance
            if ((jump as any).trialRoundDistance) {
              const currentBest = athleteBestDistanceMap.get(athleteId) || 0;
              if ((jump as any).trialRoundDistance > currentBest) {
                athleteBestDistanceMap.set(athleteId, (jump as any).trialRoundDistance);
              }
            }
            // First round distance
            if (jump.firstRoundDistance) {
              const currentBest = athleteBestDistanceMap.get(athleteId) || 0;
              if (jump.firstRoundDistance > currentBest) {
                athleteBestDistanceMap.set(athleteId, jump.firstRoundDistance);
              }
            }
            // Second round distance
            if (jump.secondRoundDistance) {
              const currentBest = athleteBestDistanceMap.get(athleteId) || 0;
              if (jump.secondRoundDistance > currentBest) {
                athleteBestDistanceMap.set(athleteId, jump.secondRoundDistance);
              }
            }
          });
        }
      });
      
      // Process banana cup jumps (all rounds: 1st, 2nd, 3rd, 4th, 5th)
      bananaCups.forEach(bananaCup => {
        if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
        
        bananaCup.competitions.forEach((competition: any) => {
          if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
          
          // Check if competition date is in range
          if (startDate && endDate && !isDateInRange(competition.date, startDate, endDate)) return;
          
          competition.jumps.forEach((jump: any) => {
            const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            const athleteId = String(athleteIdRaw);
            
            // Only process if athlete is selected
            if (selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;
            
            // Process all rounds from banana cup (can have up to 5 rounds)
            const rounds = [
              jump.firstRoundDistance,
              jump.secondRoundDistance,
              jump.thirdRoundDistance,
              jump.fourthRoundDistance,
              jump.fifthRoundDistance,
            ].filter(Boolean) as number[];
            
            rounds.forEach(distance => {
              const currentBest = athleteBestDistanceMap.get(athleteId) || 0;
              if (distance > currentBest) {
                athleteBestDistanceMap.set(athleteId, distance);
              }
            });
          });
        });
      });

      const bestDistances = Array.from(athleteBestDistanceMap.entries())
        .map(([athleteId, distance]) => {
          const athlete = athletes.find(a => a._id === athleteId);
          return {
            athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
            distance,
          };
        })
        .sort((a, b) => b.distance - a.distance);
      setBestDistancesPerAthlete(bestDistances);

      // 4. List: Average distances for each athlete for this season
      // Use the same seasonSessions from above (filtered from all sessions, not date range filtered)
      const athleteSeasonDistanceMap = new Map<string, { sum: number; count: number }>();
      
      seasonSessions.forEach(session => {
        // Process regular jumps
        if (session.jumps) {
          session.jumps.forEach(jump => {
            const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            if (jump.distance && selectedAthletes.includes(String(jumpAthleteId))) {
              const athleteId = String(jumpAthleteId);
              const existing = athleteSeasonDistanceMap.get(athleteId) || { sum: 0, count: 0 };
              existing.sum += jump.distance;
              existing.count += 1;
              athleteSeasonDistanceMap.set(athleteId, existing);
            }
          });
        }
        
        // Process competition jumps
        if (session.competitionJumps) {
          session.competitionJumps.forEach(jump => {
            const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            if (selectedAthletes.includes(String(jumpAthleteId))) {
              const athleteId = String(jumpAthleteId);
              const existing = athleteSeasonDistanceMap.get(athleteId) || { sum: 0, count: 0 };
              
              // Trial round distance
              if ((jump as any).trialRoundDistance) {
                existing.sum += (jump as any).trialRoundDistance;
                existing.count += 1;
              }
              // First round distance
              if (jump.firstRoundDistance) {
                existing.sum += jump.firstRoundDistance;
                existing.count += 1;
              }
              // Second round distance
              if (jump.secondRoundDistance) {
                existing.sum += jump.secondRoundDistance;
                existing.count += 1;
              }
              athleteSeasonDistanceMap.set(athleteId, existing);
            }
          });
        }
      });

      const avgDistances = Array.from(athleteSeasonDistanceMap.entries())
        .map(([athleteId, data]) => {
          const athlete = athletes.find(a => a._id === athleteId);
          return {
            athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
            averageDistance: data.count > 0 ? Math.round((data.sum / data.count) * 10) / 10 : 0,
          };
        })
        .sort((a, b) => b.averageDistance - a.averageDistance);
      setAvgDistancesPerAthlete(avgDistances);
    }
  }, [activeTab, sessions, startDate, endDate, selectedAthletes, athletes, bananaCups]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  // Helper function to calculate Y-axis domain (min - 10, auto max)
  const calculateYAxisDomain = (data: any[], dataKeys: string[]): [number, string] => {
    let minValue = Infinity;
    
    data.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (value !== null && value !== undefined && typeof value === 'number' && value < minValue) {
          minValue = value;
        }
      });
    });
    
    if (minValue === Infinity) {
      return [0, 'auto'];
    }
    
    return [Math.max(0, minValue - 10), 'auto'];
  };

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading training sessions data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Training Sessions Analytics</PageTitle>
        <PageDescription>
          Analyze training session data, including frequency, types, and performance metrics across different time periods.
        </PageDescription>
      </PageHeader>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? '0.25rem' : '1rem', 
        marginBottom: '1rem', 
        borderBottom: isMobile ? '1px solid var(--card-border)' : '2px solid var(--card-border)',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        ...(isMobile ? {
          marginLeft: '-1rem',
          marginRight: '-1rem',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        } : {})
      }}>
        {((isJumperMode ? ['sessions', 'location'] : ['sessions', 'location', 'athlete']) as TabType[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: isMobile ? '0.3rem 0.45rem' : '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? (isMobile ? '2px solid var(--accent-orange)' : '3px solid var(--accent-orange)') : (isMobile ? '2px solid transparent' : '3px solid transparent'),
              color: activeTab === tab ? 'var(--accent-orange)' : 'var(--text-gray)',
              fontFamily: 'Poppins',
              fontSize: isMobile ? '0.6rem' : '1rem',
              fontWeight: activeTab === tab ? 900 : 600,
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              letterSpacing: '-0.02em',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (activeTab === 'sessions' || activeTab === 'athlete') && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}
        </SelectionGrid>
      </DataSelectionSection>

      {/* SESSIONS Tab Content */}
      {activeTab === 'sessions' && (
        <>
          {/* Pie chart - full width in jumper mode, side by side in coach mode */}
          {isJumperMode ? (
            <GraphSection>
              <GraphTitle>Training Sessions by Type</GraphTitle>
              {startDate && endDate && trainingSessionsPieData.length > 0 ? (
                <GraphContainer style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <PieChart>
                      <Pie
                        data={trainingSessionsPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {trainingSessionsPieData.map((entry, index) => {
                          const colors = ['#ff9600', '#0066cc', '#00cc66'];
                          return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                        })}
                      </Pie>
                      <Tooltip />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px', fontSize: isMobile ? '0.5rem' : undefined, fontFamily: 'Poppins' }}
                        iconSize={isMobile ? 8 : 12}
                        formatter={(value) => value}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </GraphContainer>
              ) : (
                <EmptyState>No training session data available for the selected filters.</EmptyState>
              )}
            </GraphSection>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: isMobile ? '1.5rem' : '2rem', 
              marginBottom: '2rem' 
            }}>
              {/* Pie chart: Training sessions by type */}
              <GraphSection>
                <GraphTitle>Training Sessions by Type</GraphTitle>
                {startDate && endDate && trainingSessionsPieData.length > 0 ? (
                  <GraphContainer style={{ height: '400px' }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                      <PieChart>
                        <Pie
                          data={trainingSessionsPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`}
                          outerRadius={90}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {trainingSessionsPieData.map((entry, index) => {
                            const colors = ['#ff9600', '#0066cc', '#00cc66'];
                            return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                          })}
                        </Pie>
                        <Tooltip />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px', fontSize: isMobile ? '0.5rem' : undefined, fontFamily: 'Poppins' }}
                          iconSize={isMobile ? 8 : 12}
                          formatter={(value) => value}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </GraphContainer>
                ) : (
                  <EmptyState>No training session data available for the selected filters.</EmptyState>
                )}
              </GraphSection>

              {/* Bar graph: Number of training sessions by athlete - Hidden in jumper mode */}
              <GraphSection>
                <GraphTitle>Number of Training Sessions by Athlete</GraphTitle>
                {startDate && endDate && trainingSessionsByAthlete.length > 0 ? (
                  <GraphContainer>
                    <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                      <BarChart data={trainingSessionsByAthlete}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="athlete" 
                          angle={-45} 
                          textAnchor="end" 
                          height={100}
                          style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                          tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        />
                        <YAxis 
                          style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                          tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" name="Training Sessions" shape={<RoundedBar />}>
                          {trainingSessionsByAthlete.map((entry, index) => {
                            const athlete = athletes.find(a => a._id === entry.athleteId);
                            const color = athlete ? getAthleteColor(athlete, index) : '#ff9600';
                            return <Cell key={`cell-${index}`} fill={color} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </GraphContainer>
                ) : (
                  <EmptyState>No training session data available for selected athletes</EmptyState>
                )}
              </GraphSection>
            </div>
          )}

          {/* List of sessions per location */}
          <GraphSection>
            <GraphTitle>Training Sessions by Location</GraphTitle>
            {startDate && endDate && sessionsPerLocation.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {sessionsPerLocation.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.75rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                      {item.location}
                    </span>
                    <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                      {item.count} {item.count === 1 ? 'session' : 'sessions'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState>No training sessions found for the selected date range</EmptyState>
            )}
          </GraphSection>

          {/* Average training sessions per month PER ATHLETE */}
          <GraphSection>
            <GraphTitle>Average Number of Training Sessions per Month per Athlete</GraphTitle>
            {startDate && endDate && avgTrainingSessionsPerMonth.length > 0 ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={avgTrainingSessionsPerMonth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        try {
                          // Format "2024-01" to "Jan 2024"
                          const [year, month] = value.split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis 
                      domain={calculateYAxisDomain(
                      avgTrainingSessionsPerMonth,
                      athletes
                        .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                        .map(a => `${a.firstName} ${a.lastName}`)
                    )} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={isMobile ? { fontSize: '0.5rem', fontFamily: 'Poppins' } : undefined} iconSize={isMobile ? 8 : 12} />
                    {athletes
                      .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            connectNulls={true}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>No training session data available</EmptyState>
            )}
          </GraphSection>
        </>
      )}

      {/* LOCATION Tab Content */}
      {activeTab === 'location' && (
        <>
          {/* Grid layout for 3 graphs */}
          <LocationGrid>
            {/* Bar chart: Where most training sessions were done */}
            <GraphSection>
              <GraphTitle>Training Sessions by Location</GraphTitle>
            {startDate && endDate && locationBarChartData.length > 0 ? (
                <GraphContainer style={{ height: '350px' }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <BarChart data={locationBarChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="location" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      />
                      <YAxis 
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      />
                      <Tooltip />
                      <Legend wrapperStyle={isMobile ? { fontSize: '0.5rem', fontFamily: 'Poppins' } : undefined} iconSize={isMobile ? 8 : 12} />
                      <Bar dataKey="count" fill="#ff9600" name="Number of Sessions" shape={<RoundedBar />} />
                    </BarChart>
                  </ResponsiveContainer>
                </GraphContainer>
              ) : (
                <EmptyState>No location data available</EmptyState>
              )}
            </GraphSection>

            {/* List: Average distances on different locations */}
            <GraphSection>
              <GraphTitle>Average Distances by Location</GraphTitle>
              {startDate && endDate && avgDistancesPerLocation.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '350px', overflowY: 'auto' }}>
                  {avgDistancesPerLocation.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '0.75rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                        {item.location}
                      </span>
                      <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                        {item.averageDistance}m
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState>No distance data available for locations</EmptyState>
              )}
            </GraphSection>

            {/* List: Best distances at Location */}
            <GraphSection>
              <GraphTitle>Best Distances at Location</GraphTitle>
              {startDate && endDate && bestDistancesPerLocation.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '350px', overflowY: 'auto' }}>
                  {bestDistancesPerLocation.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '0.75rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                        {item.location}
                      </span>
                      <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                        {item.distance}m
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState>No best distance data available</EmptyState>
              )}
            </GraphSection>
          </LocationGrid>
        </>
      )}

      {/* ATHLETE Tab Content - Hidden in jumper mode */}
      {!isJumperMode && activeTab === 'athlete' && (
        <>
          {/* Side by side layout */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
            gap: isMobile ? '1.5rem' : '2rem' 
          }}>
            {/* Bar chart: Average participation per month */}
            <GraphSection>
              <GraphTitle>Average Training Sessions per Month by Athlete</GraphTitle>
              {startDate && endDate && athleteParticipationPerMonth.length > 0 ? (
                <GraphContainer>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <BarChart data={athleteParticipationPerMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="athlete" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend wrapperStyle={isMobile ? { fontSize: '0.5rem', fontFamily: 'Poppins' } : undefined} iconSize={isMobile ? 8 : 12} />
                      <Bar dataKey="average" name="Average Sessions per Month" shape={<RoundedBar />}>
                        {athleteParticipationPerMonth.map((entry, index) => {
                          const athlete = athletes.find(a => a._id === entry.athleteId);
                          const color = athlete ? getAthleteColor(athlete, index) : '#ff9600';
                          return <Cell key={`cell-${index}`} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </GraphContainer>
              ) : (
                <EmptyState>No athlete participation data available</EmptyState>
              )}
            </GraphSection>

            {/* List: Best distances */}
            <GraphSection>
              <GraphTitle>Best Distances by Athlete</GraphTitle>
              {startDate && endDate && bestDistancesPerAthlete.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {bestDistancesPerAthlete.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '0.75rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontFamily: 'Poppins', fontWeight: 600, color: 'var(--text-dark)' }}>
                        {item.athlete}
                      </span>
                      <span style={{ fontFamily: 'Poppins', fontWeight: 700, color: 'var(--accent-orange)' }}>
                        {item.distance}m
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState>No best distance data available</EmptyState>
              )}
            </GraphSection>
          </div>
        </>
      )}
    </AnalyticsContainer>
  );
};

export default TrainingSessionsAnalytics;
